import React from "react";
import {Box, Grid} from "@mui/material";
import {useTheme} from "../../contexts/theme";
import TransparentButton from "../CommonComponents/TransparentButton/TransparentButton";
import LoopIcon from '@mui/icons-material/Loop';
import StyledIconButton from "../CommonComponents/StyledIconButton/StyledIconButton";
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPrevious from '@mui/icons-material/SkipPrevious';
import PlayArrow from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteIcon from '@mui/icons-material/Delete';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import {formatDuration} from "../../utils/dateTimeutils";
import {AspectRatio} from "../../types/generic";
import CustomDownloadButton from "../CommonComponents/CustomDownloadButton/CustomDownloadButton";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {Clip, Highlight} from "../../types/clipTypes";

interface VideoPlayerControlsProps {
    onPlay?: () => void;
    onPause?: () => void;
    onSkip?: (amount: number) => void;
    onRewind?: (amount: number) => void;
    onAddGrabber?: () => void;
    onPost?: () => void;
    onDone?: () => void;
    onReset?: () => void;
    onDeleteModeActivation?: () => void
    onMute: (muted: boolean) => void;
    onScreenShot?: () => void;
    videoTime: number
    isPlaying: boolean;
    muted: boolean;
    onFocus?: () => void;
    onBlur?: () => void;
    aspectRatios: AspectRatio[];
    isActiveTime: boolean;
    isWindowUpdating: boolean;
    selectedAspectRatio: string;
    clip?: Clip | Highlight;
    setIsClipDownloading: any;
    editSessionId: string;
    handlePopupClose: () => void;
    doneDisabled?: boolean
}


const VideoPlayerControls: React.FC<VideoPlayerControlsProps> = ({
    onScreenShot,
    onDone,
    onDeleteModeActivation,
    onMute,
    onPause,
    onPlay,
    onPost,
    onReset,
    onSkip,
    onRewind,
    onAddGrabber,
    videoTime,
    isPlaying,
    muted,
    onFocus,
    onBlur,
    aspectRatios,
    isActiveTime,
    isWindowUpdating,
    selectedAspectRatio,
    clip,
    setIsClipDownloading,
    editSessionId,
    handlePopupClose,
    doneDisabled = false
}) => {

    const theme = useTheme();

    const iconStyle = {
        backgroundColor: 'transparent',
        fill: theme.palette.white,
    }

    const disabledIconStyle = {
        backgroundColor: 'transparent',
        fill: theme.palette.lightGrey,
    }

    return (
                <Grid
                    container
                    justifyContent="space-between"
                    sx={{
                        maxWidth: "100%",
                    }}
                >
                    <Grid item>
                        <Box sx={{ display: "flex", gap: theme.spacing.sm }}>
                            <StyledIconButton onClick={onReset} onBlur={onBlur} onFocus={onFocus}><LoopIcon sx={iconStyle}/></StyledIconButton>
                            <StyledIconButton onBlur={onBlur} onFocus={onFocus} onClick={() => !muted ? onMute(true) : onMute(false)}>
                                {!muted ?
                                    <VolumeOffIcon sx={iconStyle} /> :
                                    <VolumeUpIcon sx={iconStyle} />
                                }
                            </StyledIconButton>
                            <StyledIconButton onClick={onScreenShot} onBlur={onBlur} onFocus={onFocus}><CameraAltIcon sx={iconStyle}/></StyledIconButton>
                        </Box>
                    </Grid>

                    <Grid item>
                        <Box sx={{ display: "flex", gap: theme.spacing.sm}}>
                            <StyledIconButton onBlur={onBlur} onFocus={onFocus} onClick={onRewind ? () => onRewind(10) : () => {}} ><SkipPrevious sx={iconStyle} /></StyledIconButton>
                            <StyledIconButton onBlur={onBlur} onFocus={onFocus} onClick={isPlaying ? onPause : onPlay}>
                                {isPlaying ? <PauseIcon sx={iconStyle}/> : <PlayArrow sx={iconStyle}/>}
                                {formatDuration(videoTime, true)}
                            </StyledIconButton>
                            <StyledIconButton onBlur={onBlur} onFocus={onFocus} onClick={onSkip ? () => onSkip(10) : () => {}}><SkipNextIcon sx={iconStyle}/></StyledIconButton>
                        </Box>
                    </Grid>

                    <Grid item>
                        <Box sx={{ display: "flex", gap: theme.spacing.sm }}>
                            <StyledIconButton onBlur={onBlur} onFocus={onFocus} disabled={!isActiveTime} onClick = {onAddGrabber}><ContentCutIcon sx={isActiveTime ? iconStyle : disabledIconStyle}/></StyledIconButton>
                            <StyledIconButton onBlur={onBlur} onFocus={onFocus} onClick= {onDeleteModeActivation}><DeleteIcon sx={iconStyle}/></StyledIconButton>
                        </Box>
                    </Grid>

                    <Grid
                        item
                        md={12}
                        lg={"auto"}
                        sx={{
                            paddingTop: {md: '10px', lg:'0'},
                            display: "flex",
                            justifyContent: { xs: "center", sm: "center", md: "center" },
                            gap: theme.spacing.sm,
                        }}
                    >
                        <CustomDownloadButton
                            selectedClipsAspectRatio={selectedAspectRatio}
                            marginLeft={theme.spacing.md}
                            text="Download"
                            additionalComponent={
                                <ArrowDropDownIcon sx={{ color: 'white' }} />
                            }
                            setIsClipDownloading={setIsClipDownloading}
                            clips={[clip]}
                            isProductionNeeded={true}
                            editSessionId={editSessionId}
                            handlePopupClose={handlePopupClose}
                            width={'120px'}
                            editorDownload={true}
                        />
                        <TransparentButton onClick={onPost} onBlur={onBlur} onFocus={onFocus} text='Post' width={'120px'}/>
                        <TransparentButton disabled={isWindowUpdating || doneDisabled} onClick={onDone} onBlur={onBlur} onFocus={onFocus} text='Done' width={'120px'}/>
                    </Grid>
                </Grid>
    );

}

export default VideoPlayerControls;